import { useTheme } from "components/provider/theme";
import { API_URL } from "env";
import React from "react";

export interface AccountUserData {
    img: {
        file: File | null;
        url?: string;
    },
    firstName: string;
    lastName: string;
    email: string;
    position: string;
    phoneNumber: string;
    country: string;
    city: string;
    address: string;
    accountType: 'free' | 'pro' | 'premium';
    password: string;
    postalCode?: string;
}

interface CreateAccountModalProps {
    returnData: (data: AccountUserData) => void;
}

const CreateAccountModalContent: React.FC<CreateAccountModalProps> = ({ returnData }) => {
    const [formData, setFormData] = React.useState<AccountUserData>({
        img: { file: null, url: '' },
        firstName: '',
        lastName: '',
        email: '',
        position: '',
        phoneNumber: '',
        country: '',
        city: '',
        address: '',
        accountType: 'free',
        password: ''
    });
    const { theme } = useTheme();
    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] || null;
        if (file) {
            const url = URL.createObjectURL(file);
            setFormData(prev => ({ ...prev, img: { file, url } }));
        }
    };
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        let avatarBase64 = "";
        if (formData.img.file) {
            const file = formData.img.file;
            avatarBase64 = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(file);
            });
        }

        const createUser = await fetch(`${API_URL}api/v1/be/user`, {
            method: 'POST',
            headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify({
            first_name: formData.firstName,
            last_name: formData.lastName,
            job_title: formData.position,
            email: formData.email,
            phone: formData.phoneNumber,
            country: formData.country,
            city: formData.city,
            address: formData.address,
            postal_code: formData.postalCode || "",
            password: formData.password,
            password_confirmation: formData.password,
            avatar: avatarBase64,
            })
        });
        const result = await createUser.json();
        returnData(formData);
    };

    return (
        <div className={`${theme === 'dark' ? '#1f2937' :'bg-white'} overflow-y-auto max-h-[80vh] scrollbar-hide`}
            style={{
                msOverflowStyle: "none",
                scrollbarWidth: "none",
            }}
        >
            <form className="p-6" onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="md:col-span-2">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Thông tin cá nhân</h3>
                    </div>
                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Ảnh đại diện</label>
                        <div className="flex items-center space-x-6">
                            <div className="flex-shrink-0">
                                <div className={`${theme === 'dark' ? 'bg-gray-700' :'bg-gray-200'} h-20 w-20 rounded-full  border-2 border-gray-300 flex items-center justify-center overflow-hidden`}>
                                    {formData.img.url ? (
                                        <img src={formData.img.url} alt="avatar" className="h-20 w-20 rounded-full object-cover" />
                                    ) : (
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-user h-8 w-8 text-gray-400">
                                            <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"></path>
                                            <circle cx="12" cy="7" r="4"></circle>
                                        </svg>
                                    )}
                                </div>
                            </div>
                            <div className="flex-1">
                                <label className="cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-upload h-4 w-4 mr-2">
                                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                        <polyline points="17 8 12 3 7 8"></polyline>
                                        <line x1="12" x2="12" y1="3" y2="15"></line>
                                    </svg>
                                    Chọn ảnh
                                    <input type="file" accept="image/*" className="sr-only" onChange={handleFileChange} />
                                </label>
                                <p className="mt-2 text-xs text-gray-500">PNG, JPG, GIF tối đa 5MB</p>
                            </div>
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Họ <span className="text-red-500">*</span></label>
                        <input
                            type="text"
                            name="firstName"
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Nhập họ"
                            value={formData.firstName}
                            onChange={handleChange}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Tên <span className="text-red-500">*</span></label>
                        <input
                            type="text"
                            name="lastName"
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Nhập tên"
                            value={formData.lastName}
                            onChange={handleChange}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Chức danh</label>
                        <input
                            type="text"
                            name="position"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Nhập chức danh"
                            value={formData.position}
                            onChange={handleChange}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Email <span className="text-red-500">*</span></label>
                        <input
                            type="email"
                            name="email"
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Nhập địa chỉ email"
                            value={formData.email}
                            onChange={handleChange}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Số điện thoại <span className="text-red-500">*</span></label>
                        <input
                            type="tel"
                            name="phoneNumber"
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Nhập số điện thoại"
                            value={formData.phoneNumber}
                            onChange={handleChange}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Quốc gia <span className="text-red-500">*</span></label>
                        <input
                            type="text"
                            name="country"
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Nhập quốc gia"
                            value={formData.country}
                            onChange={handleChange}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Thành phố <span className="text-red-500">*</span></label>
                        <input
                            type="text"
                            name="city"
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Nhập thành phố"
                            value={formData.city}
                            onChange={handleChange}
                        />
                    </div>
                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Địa chỉ <span className="text-red-500">*</span></label>
                        <input
                            type="text"
                            name="address"
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Nhập địa chỉ đầy đủ"
                            value={formData.address}
                            onChange={handleChange}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Mã bưu điện</label>
                        <input
                            type="text"
                            name="postalCode"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Nhập mã bưu điện"
                            value={formData.postalCode}
                            onChange={handleChange}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Loại tài khoản</label>
                        <select
                            title="Loại tài khoản"
                            name="accountType"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            value={formData.accountType}
                            onChange={handleChange}
                        >
                            <option value="free">Free</option>
                            <option value="premium">Premium</option>
                            <option value="pro">Pro</option>
                        </select>
                    </div>
                    <div className="md:col-span-2 mt-6">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Thông tin bảo mật</h3>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Mật khẩu <span className="text-red-500">*</span></label>
                        <div className="relative">
                            <input
                                type="password"
                                name="password"
                                required
                                minLength={6}
                                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="Nhập mật khẩu (tối thiểu 6 ký tự)"
                                value={formData.password}
                                onChange={handleChange}
                            />
                            <button title="showmatkhau1" type="button" className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-eye h-4 w-4">
                                    <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"></path>
                                    <circle cx="12" cy="12" r="3"></circle>
                                </svg>
                            </button>
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Xác nhận mật khẩu <span className="text-red-500">*</span></label>
                        <div className="relative">
                            <input
                                type="password"
                                name="confirmPassword"
                                required
                                minLength={6}
                                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="Nhập lại mật khẩu"
                            // Not in AccountUserData, so not controlled
                            />
                            <button title="showmatkhau2" type="button" className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-eye h-4 w-4">
                                    <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"></path>
                                    <circle cx="12" cy="12" r="3"></circle>
                                </svg>
                            </button>
                        </div>
                    </div>
                    <div className="text-white w-full md:col-span-2">
                        <button
                            type="submit"
                            className="w-1/3 float-right bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                        >
                            Tạo tài khoản
                        </button>
                    </div>
                </div>
            </form>

        </div>
    )
}

export default CreateAccountModalContent;